import { LTI_CONTEXT_UPDATE_EVENT } from './detection';
import { isInstructorRole } from './types';

const STORAGE_KEY = '@lti/context';

interface LtiContextData {
  roles: string[];
  contextTitle: string;
  quizTitle?: string;
  isInstructor: boolean;
}

function emitUpdate(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(LTI_CONTEXT_UPDATE_EVENT));
  } catch {
    // ignore (e.g. environments without CustomEvent)
  }
}

/**
 * Client-side LTI context manager using SessionStorage.
 * Only stores UI-relevant data (roles, context title).
 * Sensitive data (AGS endpoint, credentials) is stored in the JWE session cookie only.
 *
 * Pattern: apps/quiz-app/src/shared/moodle/MoodleContextManager.ts
 */
class LtiContextManagerImpl {
  initialize(data: Omit<LtiContextData, 'isInstructor'>): void {
    const isInstructor = isInstructorRole(data.roles);

    const contextData: LtiContextData = { ...data, isInstructor };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(contextData));
    } catch {
      // SessionStorage unavailable (e.g., incognito mode in some browsers)
    }
    emitUpdate();
  }

  isInLti(): boolean {
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  }

  getContext(): LtiContextData | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as LtiContextData;
    } catch {
      return null;
    }
  }

  isInstructor(): boolean {
    return this.getContext()?.isInstructor ?? false;
  }

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    emitUpdate();
  }
}

export const ltiContextManager = new LtiContextManagerImpl();
